import { isEmpty } from "lodash";

/**
 * Parameters for sanitizing numeric input
 */
export interface SanitizeNumericInputParams {
    /** Whether to allow decimal numbers (default: true) */
    allowDecimals?: boolean;
    /** Whether to allow negative numbers (default: false) */
    allowNegative?: boolean;
    /** Whether to allow leading comma (default: false) */
    allowleadingComma?: boolean;
    /** Whether to allow leading zero (default: false) */
    allowLeadingZero?: boolean;
    /** Whether to allow both dot and comma as decimal separators (default: false) */
    allowDotOrComma?: boolean;
    /** Maximum length of the input */
    maxLength?: number;
}

/**
 * Sanitizes and validates numeric input value
 * Removes non-numeric characters and ensures the value conforms to specified constraints
 *
 * @param value - The input value to sanitize
 * @param params - Sanitization parameters
 * @returns The sanitized value or undefined if empty
 */
export const sanitizeNumericInput = (value: string, params?: SanitizeNumericInputParams): string | undefined => {
    const {
        allowDecimals = true,
        allowNegative = false,
        maxLength,
        allowleadingComma = false,
        allowLeadingZero = false,
        allowDotOrComma = false,
    } = params ?? {};

    if (!value) {
        return undefined;
    }

    // values with commas fail to conver to numbers. Converting to dots for manipulations.
    const usesComma = value.includes(",");
    value = value.replaceAll(",", ".");

    // Convert leading zeros (e.g., 00-09, -078) into a number, preserving negative sign if present
    if (!allowLeadingZero) {
        const isNegative = value.startsWith("-");
        if (isNegative) {
            value = value.substring(1);
        }

        // Convert 00 - 09 into a number
        if (value.match(/^0\d/g)) {
            value = String(Number(value));
        }

        if (isNegative) {
            value = `-${value}`;
        }
    }

    // Remove minus signs that are not at the start
    if (allowNegative && value.includes("-")) {
        value = value
            .split("")
            .map((char, index) => {
                if (index !== 0 && char === "-") {
                    return "";
                }
                return char;
            })
            .join("");
    }

    // Remove non-numeric characters
    // Remove characters that are not "-" if allowNegative is set to true
    let regex = allowNegative ? /[^0-9-]/g : /\D/g;
    let result = value.replace(regex, "");

    if (allowDecimals) {
        // Remove non-numeric characters
        // Remove characters that are not "." if allowDecimals is set to true
        // Remove characters that are not "-" if allowNegative is set to true
        regex = allowNegative ? /[^0-9.-]/g : /[^0-9.]/g;
        result = value.replace(regex, "");

        // Remove duplicate dots
        result = result.replace(/(\..*)\./g, "$1");

        if (!allowleadingComma) {
            // By adding "." at the start, add 0 beforehand
            if (result.startsWith(".")) {
                result = `0${result}`;
            }
            // If negative values are allowed and it starts with "-."
            // add 0 between "-" and "."
            else if (allowNegative && result.startsWith("-.")) {
                result = `-0${result.substring(1, result.length)}`;
            }
        }
    }

    // Truncate to maxLength
    if (maxLength && maxLength > 0 && result.length > maxLength) {
        result = result.substring(0, maxLength);
    }

    if (isEmpty(result)) {
        return undefined;
    }

    if (usesComma && allowDotOrComma) {
        result = result.replaceAll(".", ",");
    }

    return result;
};

/**
 * Checks if a value is a valid number
 * @param value - The value to check
 * @returns True if the value is a valid number
 */
export const isValidNumber = (value: string | number | null | undefined): boolean => {
    if (value === "" || value === null || value === undefined) {
        return false;
    }
    const num = Number(value);
    return !isNaN(num) && isFinite(num);
};
